<template>
    <app-popover class="tiny-search" ref="popover" :inherit-width="true" :container-padding="results.length === 0" :close-on-scroll="false">
        <template #default>
            <input type="hidden" :name="name" v-model="inputValue" v-if="name"/>
            <form-input :placeholder="placeholder" type="text" ref="input" v-model.trim="searchValue" v-debounce="searchChanged" :events="events" v-bind="$attrs"
                aria-label="Suchbegriff"/>
            <i v-if="clearable" v-interact="() => emit('clear')" class="material-icons" aria-hidden="true">close</i>
            <i v-else class="material-icons" aria-hidden="true" role="img">search</i>
        </template>
        <template #container>
            <loading-indicator text="Suche läuft..." v-if="searching"/>
            <span v-else-if="error">Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.</span>
            <span v-else-if="!searching && results.length === 0">Keine Ergebnisse gefunden</span>
            <table v-else>
                <colgroup>
                    <col :style="{width: size + '%'}" v-for="size in columnSizes"/>
                </colgroup>
                <tbody>
                <tr v-for="(result, key) in results" @click="resultSelect(key)">
                    <td class="text-overflow" :class="{'text-right' : result.columns.length > 1 && columnkey +1 === result.columns.length}"
                        v-for="(column, columnkey) in result.columns" v-tooltip="column">
                        {{column}}
                    </td>
                </tr>
                </tbody>
            </table>
        </template>
    </app-popover>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import {XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import axios from 'axios';
    import {computed, ref, watch} from 'vue';
    import AppPopover from './AppPopover.vue';
    import FormInput from './form/FormInput.vue';
    import LoadingIndicator from './LoadingIndicator.vue';

    const portuna = usePortuna();

    const emit = defineEmits<{
        (eventName: 'apply', data: AppSearchTinyData): void;
        (eventName: 'update:modelValue', input: string | number): void;
        (eventName: 'clear'): void;
    }>();

    const props = withDefaults(
        defineProps<{
            searchUrl?: string;
            name?: string;
            modelValue?: number | string;
            columnSizes?: number[];
            applySingleResult?: boolean;
            hideEmpty?: boolean;
            applySearch?: boolean;
            placeholder?: string;
            clearable?: boolean | number;
        }>(),
        {
            modelValue: 0,
            applySingleResult: true,
            hideEmpty: false,
            applySearch: false,
            placeholder: '',
            name: '',
            searchUrl: '',
            clearable: false,
            columnSizes: () => ([]),
        },
    );

    const input   = ref<IFormInputContext>();
    const popover = ref<IAppPopoverContext>();

    const searching     = ref<boolean>(false);
    const searched      = ref<boolean>(false);
    const error         = ref<boolean>(false);
    const openOnFocus   = ref<boolean>(false);
    const searchValue   = ref<string>('');
    const currentSearch = ref<string>('');
    const inputValue    = ref<number | string>('');
    const results       = ref<TinySearchResult[]>([]);
    const cleared       = ref<boolean>(false);

    const events = computed(() =>
    {
        return {
            focus: searchFocused,
            click: searchFocused,
            keydown: (event: KeyboardEvent) =>
            {
                if (event.key === 'Tab')
                {
                    close();
                }
            },
        };
    });

    const close = () =>
    {
        popover.value!.close();
    };

    const open = () =>
    {
        if (props.hideEmpty && searched.value && 0 === results.value.length)
        {
            return;
        }

        popover.value!.open();
    };

    const resultSet = (newInputValue: number | string, newSearchValue: string) =>
    {
        if ((newInputValue === 0 || newInputValue === '') && newSearchValue === '')
        {
            results.value = [];
            cleared.value = true;
        }
        else
        {
            cleared.value = false;
        }

        inputValue.value    = newInputValue;
        searchValue.value   = newSearchValue;
        currentSearch.value = newSearchValue;

        emit('update:modelValue', inputValue.value);

        emit('apply', {
            value: inputValue.value,
            label: searchValue.value,
        } as AppSearchTinyData);
    };

    /**
     * search field focused, if a request was sent then re-open the popover
     */
    const searchFocused = () =>
    {
        if (searched.value && openOnFocus.value && !cleared.value)
        {
            open();
        }

        openOnFocus.value = true;
    };

    const clear = () =>
    {
        currentSearch.value = '';
        searchValue.value   = '';

        let value = 'number' === typeof inputValue.value ? 0 : '';

        emit('update:modelValue', value);

        emit('apply', {
            value: value,
            label: '',
        } as AppSearchTinyData);
    };

    const setEntry = (entry: TinySearchResult, userSelect = false) =>
    {
        if (userSelect)
        {
            openOnFocus.value = false;

            input.value!.focus();
        }

        resultSet(entry.value, entry.preview);
    };

    const resultClear = () =>
    {
        resultSet(0, '');
    };

    const resultSelect = (index: number) =>
    {
        close();

        setEntry(results.value[index], true);
    };

    const search = (fillMode = false) =>
    {
        if (!fillMode && searchValue.value.length === 0)
        {
            searched.value = false;

            resultClear();

            close();

            return;
        }

        // reset values
        results.value       = [];
        searching.value     = true;
        error.value         = false;
        currentSearch.value = searchValue.value;
        let data;

        if (fillMode)
        {
            data = {fill: inputValue.value};
        }
        else
        {
            data = {search: currentSearch.value};
        }

        axios
        .post(props.searchUrl, data)
        .then(({
            data: {
                status,
                data,
            },
        }) =>
        {
            if (status !== XHR_STATUS_DEFAULT)
            {
                throw new Error(status);
            }

            if (fillMode)
            {
                if (data.entry)
                {
                    setEntry(data.entry);
                }
                else if (props.hideEmpty)
                {
                    close();
                }
            }
            else
            {
                results.value = data.entries || [];

                if (props.applySingleResult && results.value.length === 1)
                {
                    resultSelect(0);
                }
                else if (props.hideEmpty && !results.value.length)
                {
                    close();
                }
            }
        })
        .catch((err) =>
        {
            console.error('[TinySearch-' + props.name + '] ' + props.searchUrl + ': ', err);

            error.value = true;

            if (fillMode)
            {
                resultClear();
            }
        })
        .finally(() =>
        {
            searched.value  = !fillMode;
            searching.value = false;
        });
    };

    const searchChanged = () =>
    {
        // if search value not changed (like press CTRL, SHIFT, TAB) don't send xhr
        if (currentSearch.value === searchValue.value)
        {
            return;
        }

        cleared.value    = false;
        inputValue.value = searchValue.value;

        open();

        search();

        if (props.applySearch)
        {
            resultSet(searchValue.value, searchValue.value);
        }
    };

    const setSearchValue = (value: string) =>
    {
        searchValue.value = value;
    };

    inputValue.value = props.modelValue;

    if (inputValue.value)
    {
        search(true);
    }

    watch(() => props.modelValue, (newValue: number | string) =>
    {
        // check only changes from v-model
        if (props.modelValue !== inputValue.value)
        {
            inputValue.value = newValue;
            search(true);
        }
    });

    defineExpose(Object.assign({}, portuna, {
        componentName: 'AppSearchTiny',
        resultClear,
        clear,
        open,
        close,
        setSearchValue,
        input,
        popover,
    }));
</script>